// §D13 (Section 17) — manager resolve dialog for an IN_TRANSIT_EXCEPTION return.
// Loads nothing itself — the caller passes the already-fetched IReturnDetail.
// Liability split mirrors the backend: BUYER_ABSORB → 0% + REJECTED; the rest →
// default 100% (override allowed) + AWAITING_RESOLUTION.

import {
  ActionIcon, Alert, Box, Button, Card, Divider, Drawer, Grid, Group, Image,
  Modal, NumberInput, SimpleGrid, Stack, Text, Textarea, TextInput, Title, UnstyledButton
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { HelpIcon, i18n, nextStepToast, showNotification } from "@dehub/theme";
import { CaretLeftIcon, CaretRightIcon, ImageIcon, WarningIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReturnApi } from "../../apis";
import { currencyFormat } from "../../utils";
import { dateTimeSecFormat } from "../../utils/date";
import { message } from "../../utils/message";
import { DotPill } from "../delivery/atoms";
import type { IReturnDetail, TransitExceptionResolution } from "../../apis/return";

type TFn = (k: string, o?: Record<string, unknown>) => string;

const exceptionTypeLabel = (v?: string | null) => (v ? i18n.t(`returns:transitResolve.exceptionType.${v}`) : "-");
const reportedByLabel = (v?: string | null) => (v ? i18n.t(`returns:transitResolve.reportedBy.${v}`) : "-");
const shippingLabel = (v?: string | null) => (v ? i18n.t(`returns:shipping.responsibility.${v}`) : "-");

const resolutionCards = (t: TFn): { value: TransitExceptionResolution; title: string; desc: string; color: string }[] => [
  { value: "SELLER_ABSORB",     title: t("transitResolve.card.sellerAbsorb"),     desc: t("transitResolve.card.sellerAbsorbDesc"),     color: "brand" },
  { value: "BUYER_ABSORB",      title: t("transitResolve.card.buyerAbsorb"),      desc: t("transitResolve.card.buyerAbsorbDesc"),      color: "brand" },
  { value: "INSURANCE_PAYOUT",  title: t("transitResolve.card.insurancePayout"),  desc: t("transitResolve.card.insurancePayoutDesc"),  color: "brand" },
  { value: "CARRIER_LIABILITY", title: t("transitResolve.card.carrierLiability"), desc: t("transitResolve.card.carrierLiabilityDesc"), color: "brand" }
];

const fmt = (s?: string | null) => dateTimeSecFormat(s);

interface Props {
  detail: IReturnDetail | null;
  opened: boolean;
  onClose: () => void;
  onResolved: () => void;
}

function FieldRow ({ label, value }: { label: string; value: React.ReactNode }){
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap" align="flex-start">
      <Text size="sm" c="var(--nw-text-2)">{label}</Text>
      <Box ta="right">{typeof value === "string" ? <Text size="sm" fw={500}>{value}</Text> : value}</Box>
    </Group>
  );
}

export function TransitExceptionResolveModal ({ detail, opened, onClose, onResolved }: Props){
  const { t } = useTranslation("returns");
  const RESOLUTION_CARDS = resolutionCards(t);
  const [resolution, setResolution] = useState<TransitExceptionResolution | null>(null);
  const [insuranceClaimId, setInsuranceClaimId] = useState("");
  const [refundPercent, setRefundPercent] = useState(100);
  const [auditReason, setAuditReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Photo zoom
  const [gallery, setGallery] = useState<string[]>([]);
  const [gIdx, setGIdx] = useState(0);
  const [zoomOpen, zoomH] = useDisclosure(false);
  const openZoom = (photos: string[], start: number) => { setGallery(photos); setGIdx(start); zoomH.open(); };
  const gPrev = () => setGIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const gNext = () => setGIdx((i) => (i + 1) % gallery.length);

  // Reset the form whenever the dialog (re)opens for a return.
  useEffect(() => {
    if (opened) {
      setResolution(null);
      setInsuranceClaimId("");
      setRefundPercent(100);
      setAuditReason("");
      setShowErrors(false);
    }
  }, [opened, detail?.id]);

  if (!detail) return null;

  const isBuyerAbsorb = resolution === "BUYER_ABSORB";
  const isInsurance = resolution === "INSURANCE_PAYOUT";
  const effectivePercent = isBuyerAbsorb ? 0 : refundPercent;
  const total = detail.totalRequestedAmount ?? 0;
  const previewAmount = Math.round(total * effectivePercent) / 100;

  const onPickResolution = (value: TransitExceptionResolution) => {
    setResolution(value);
    setRefundPercent(value === "BUYER_ABSORB" ? 0 : 100);
  };

  const auditOk = auditReason.trim().length >= 3;
  const insuranceOk = !isInsurance || insuranceClaimId.trim().length > 0;
  const valid = !!resolution && auditOk && insuranceOk;

  const photos = detail.transitExceptionEvidencePhotos ?? [];

  const submit = async () => {
    setShowErrors(true);
    if (!resolution) { showNotification({ color: "red", title: t("toast.error"), message: t("transitResolve.pickResolution") }); return; }
    if (!auditOk) { showNotification({ color: "red", title: t("toast.error"), message: t("transitResolve.auditMin") }); return; }
    if (!insuranceOk) { showNotification({ color: "red", title: t("toast.error"), message: t("transitResolve.insuranceRequired") }); return; }
    try {
      setBusy(true);
      await ReturnApi.resolveTransitException(detail.id, {
        resolution,
        insuranceClaimId: isInsurance ? insuranceClaimId.trim() : (insuranceClaimId.trim() || null),
        refundPercent   : effectivePercent,
        auditReason     : auditReason.trim(),
        expectedVersion : detail.version
      });
      nextStepToast("order.returns-transit-exceptions.toast.resolve", { fallback: { title: t("toast.success"), message: t("transitResolve.resolved") } });
      onResolved();
      onClose();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer opened={opened} onClose={onClose} position="right" size={720} title={`${t("transitResolve.title")} · ${detail.returnNumber}`}>
      <Stack gap="md">
        {/* EXCEPTION CONTEXT */}
        <Card withBorder radius="md" padding="sm">
          <Title order={6} mb="xs">{t("transitResolve.exceptionInfo")}</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <FieldRow label={t("resolutionPanel.type")} value={
              <DotPill tone="red">{exceptionTypeLabel(detail.transitExceptionType)}</DotPill>
            } />
            <FieldRow label={t("transitResolve.reportedByLabel")} value={reportedByLabel(detail.transitExceptionReportedBy)} />
            <FieldRow label={t("detail.field.date")} value={fmt(detail.transitExceptionReportedAt)} />
            <FieldRow label={t("transitResolve.carrierRef")} value={detail.transitExceptionCarrierRef ?? "-"} />
          </SimpleGrid>
          {detail.transitExceptionDescription ? (
            <Box mt="xs">
              <Text size="sm" c="var(--nw-text-2)" mb={2}>{t("transitResolve.description")}</Text>
              <Text size="sm">{detail.transitExceptionDescription}</Text>
            </Box>
          ) : null}
          {photos.length ? (
            <Group gap="sm" wrap="wrap" mt="sm">
              {photos.map((url, i) => (
                <UnstyledButton key={i} onClick={() => openZoom(photos, i)} aria-label={t("transitResolve.zoom")}>
                  <Image src={url} w={88} h={66} radius="sm" fit="cover" fallbackSrc="https://placehold.co/176x132?text=No+image" />
                </UnstyledButton>
              ))}
            </Group>
          ) : null}
        </Card>

        {/* SHIPMENT CONTEXT */}
        <Card withBorder radius="md" padding="sm">
          <Title order={6} mb="xs">{t("transitResolve.shipmentInfo")}</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <FieldRow label={t("filters.buyer")} value={detail.buyerBusinessName ?? "-"} />
            <FieldRow label={t("transitResolve.shippingChannel")} value={detail.shippingResponsibility ? shippingLabel(detail.shippingResponsibility) : "-"} />
            <FieldRow label={t("transitResolve.pickupDate")} value={fmt(detail.pickupScheduledAt)} />
            <FieldRow label={t("transitResolve.tracking")} value={detail.pickupTrackingNumber ?? "-"} />
            <FieldRow label={t("transitResolve.carrier")} value={detail.pickupCarrier ?? "-"} />
            <FieldRow label={t("transitResolve.originalRefund")} value={
              `${detail.refundPercent != null ? `${detail.refundPercent}% · ` : ""}${currencyFormat(detail.refundAmount ?? 0)} / ${currencyFormat(total)}`
            } />
          </SimpleGrid>
        </Card>

        {/* RESOLUTION CHOICE */}
        <Box>
          <Text size="sm" fw={600} mb={6}>{t("transitResolve.resolution")}<HelpIcon helpKey="order.returns-transit-exceptions.types.resolution" /></Text>
          <Grid>
            {RESOLUTION_CARDS.map((r) => {
              const active = resolution === r.value;
              return (
                <Grid.Col key={r.value} span={{ base: 12, sm: 6 }}>
                  <Card
                    withBorder radius="md" padding="sm"
                    onClick={() => onPickResolution(r.value)}
                    role="radio"
                    aria-checked={active}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPickResolution(r.value); } }}
                    styles={{ root: {
                      cursor: "pointer",
                      borderColor: active ? "var(--mantine-primary-color-filled)" : undefined,
                      background: active ? "var(--mantine-primary-color-light)" : undefined
                    } }}
                  >
                    <Text size="sm" fw={600}>{r.title}</Text>
                    <Text size="sm" c="var(--nw-text-2)">{r.desc}</Text>
                  </Card>
                </Grid.Col>
              );
            })}
          </Grid>
          {showErrors && !resolution ? <Text size="sm" c="red" mt={4}>{t("transitResolve.pickResolution")}</Text> : null}
        </Box>

        {/* INSURANCE CLAIM (INSURANCE_PAYOUT only) */}
        {isInsurance ? (
          <TextInput
            label={t("transitResolve.insuranceClaimId")}
            required
            value={insuranceClaimId}
            onChange={(e) => setInsuranceClaimId(e.currentTarget.value)}
            error={showErrors && !insuranceOk ? t("transitResolve.required") : undefined}
            maxLength={128}
          />
        ) : null}

        {/* REFUND OVERRIDE + preview */}
        <Group align="flex-end" gap="md" wrap="wrap">
          <NumberInput
            label={t("transitResolve.refundPercent")}
            value={effectivePercent}
            onChange={(v) => setRefundPercent(typeof v === "number" ? v : 0)}
            min={0} max={100}
            disabled={isBuyerAbsorb || !resolution}
            w={160}
          />
          <Text size="sm" pb={6}>
            {t("transitResolve.refundAmount")}: <Text span fw={600}>{currencyFormat(previewAmount)}</Text>
          </Text>
        </Group>

        {/* AUDIT REASON */}
        <Textarea
          label={t("transitResolve.auditReason")}
          required
          value={auditReason}
          onChange={(e) => setAuditReason(e.currentTarget.value)}
          error={showErrors && !auditOk ? t("transitResolve.min3") : undefined}
          autosize minRows={2}
        />

        {/* SIDE EFFECTS */}
        {resolution ? (
          <Alert color="gray" variant="light" icon={<WarningIcon size={16} />} title={t("transitResolve.afterResolveTitle")}>
            <Text size="sm">
              {isBuyerAbsorb
                ? t("transitResolve.afterRejected")
                : t("transitResolve.afterResolution")}
            </Text>
          </Alert>
        ) : null}

        <Divider />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>{t("resolutionPanel.cancel")}</Button>
          <Button onClick={submit} loading={busy} disabled={!valid}>{t("transitResolve.resolve")}</Button>
        </Group>
      </Stack>

      {/* Photo zoom */}
      <Modal opened={zoomOpen} onClose={zoomH.close} size="lg" centered
        title={<Group gap="xs"><ImageIcon size={18} /><Text fw={600}>{t("transitResolve.evidencePhoto")}</Text></Group>}>
        {gallery.length ? (
          <Stack gap="sm">
            <Image src={gallery[gIdx]} radius="md" mah={440} fit="contain" fallbackSrc="https://placehold.co/600x400?text=No+image" />
            {gallery.length > 1 ? (
              <Group justify="center" gap="md">
                <ActionIcon variant="default" onClick={gPrev} aria-label={t("transitResolve.prev")}><CaretLeftIcon size={18} /></ActionIcon>
                <Text size="sm">{gIdx + 1} / {gallery.length}</Text>
                <ActionIcon variant="default" onClick={gNext} aria-label={t("transitResolve.next")}><CaretRightIcon size={18} /></ActionIcon>
              </Group>
            ) : null}
          </Stack>
        ) : null}
      </Modal>
    </Drawer>
  );
}
